import {Button} from 'reactstrap';
import {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';

import {Layout} from 'src/components/Layout';
import {ToggleSwitch} from 'src/components/ToggleSwitch';
import {AgentSettings} from 'src/types';
import {getAgentSettings, saveAgentSettings} from 'src/api/agentSettings';
import {WIZARD_COMPLETE_PATH} from 'src/routes';

import './styles.css';

export const AgentSettingsPage = ({isWizard = true}: {isWizard?:boolean}) => {
  const [loading, setLoading] = useState(false);
  const [agentSettings, setAgentSettings] = useState<AgentSettings>();
  const navigate = useNavigate();
  const fetchAgentSettings = async () => {
    setLoading(true);
    const response = await getAgentSettings();
    setLoading(false);
    if (response.status === 'success') {
      setAgentSettings(response.data);
    }
  };

  useEffect(() => {
    fetchAgentSettings();
  }, []);

  const setExpungeScanSessions = async (value: boolean) => {
    if (agentSettings) {
      const updates: AgentSettings = {
        ...agentSettings,
        expungeScanSessions: value,
      };
      await storeAgentSettings(updates);
    }
  };

  const setRecursivelyScanArchives = async (value: boolean) => {
    if (agentSettings) {
      const updates: AgentSettings = {
        ...agentSettings,
        recursiveScan: value,
      };
      await storeAgentSettings(updates);
    }
  };

  const setRescanArchives = async (value: boolean) => {
    if (agentSettings) {
      const updates: AgentSettings = {
        ...agentSettings,
        rescanArchive: value,
      };
      await storeAgentSettings(updates);
    }
  };

  const storeAgentSettings = async (updates: AgentSettings) => {
    await saveAgentSettings(updates);
    await fetchAgentSettings();
  };

  return (
    <Layout title='Agent Configuration' loading={loading}>
      {!loading && agentSettings &&
        <div className='AgentSettings'>
          <div>
            { isWizard ?
              'Great, now that we&apos;ve defined your namespaces, let&apos;s configure your default agent settings.' :
              'Configure your default agent settings.'
            }
          </div>
          <div className='AgentSettings__setting-holder'>
            <div className='AgentSettings__toggle-holder'>
              <ToggleSwitch id="expungeScanSessions" name="expungeScanSessions"
                isChecked={agentSettings?.expungeScanSessions}
                onChange={setExpungeScanSessions}
              />
            </div>
            <div className='AgentSettings__explain-block'>
              <span className='AgentSettings__field-text'>Expunge Scan Sessions</span>
              <span className='AgentSettings__field-desc'>Allows agents to upload new data and replace old data. It is best to enable this for CI/CD integrations and environments where applications rapidly change</span>
            </div>
          </div>
          <div className='AgentSettings__setting-holder'>
            <div className='AgentSettings__toggle-holder'>
              <ToggleSwitch id="recursivelyScanArchives" name="recursivelyScanArchives"
                isChecked={agentSettings?.recursiveScan}
                onChange={setRecursivelyScanArchives}
              />
            </div>
            <div className='AgentSettings__explain-block'>
              <span className='AgentSettings__field-text'>Recursively Scan Archives</span>
              <span
                className='AgentSettings__field-desc'>When enabled, agents unpack nested archives, such as über JARs</span>
            </div>
          </div>
          <div className='AgentSettings__setting-holder'>
            <div className='AgentSettings__toggle-holder'>
              <ToggleSwitch id="rescanArchives" name="rescanArchives"
                isChecked={agentSettings?.rescanArchive}
                onChange={setRescanArchives}
              />
            </div>
            <div className='AgentSettings__explain-block'>
              <span className='AgentSettings__field-text'>Rescan Archives</span>
              <span className='AgentSettings__field-desc'>Perform a new scan of all archives, even if they have been previously scanned and exist within the CodeLogic database</span>
            </div>
          </div>
          {
            isWizard &&
            <Button className='WelcomeWizard__continue-button' color='primary' onClick={() => navigate(WIZARD_COMPLETE_PATH)}>Next</Button>
          }
        </div>
      }
    </Layout>
  );
};
